import { Avatar, CheckCircle, Mail, Phone } from '@chain-zen/ui'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react'
import React, { useState } from 'react'

import { useUserQuery } from '../../../hooks/use-user-query'
import { DirectMessageButton } from './direct-message-button'

interface UserInfoTooltipProps {
  children: React.ReactNode
  showTooltip?: boolean
  user: {
    email?: string
    id?: string
    name?: string
    online?: boolean
    phone?: string
    photo?: string | null
    userType?: string | null
  }
  workspace?: { id?: string; isVerified?: boolean; name?: string }
}

export const UserInfoTooltip = ({ children, showTooltip, user, workspace }: UserInfoTooltipProps) => {
  const { myDetails } = useUserQuery()
  const [isOpen, setIsOpen] = useState(false)
  const isUserTypeWorkspace = user.userType === 'workspace'

  const { x, y, refs, context } = useFloating({
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ['left', 'bottom', 'top'],
        padding: 5
      }),
      shift({
        crossAxis: true,
        mainAxis: true,
        padding: 5
      })
    ],
    onOpenChange: setIsOpen,
    open: isOpen,
    placement: 'right-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate
  })

  const hover = useHover(context, {
    delay: { close: 150, open: 300 },
    handleClose: safePolygon({
      blockPointerEvents: true
    }),
    mouseOnly: true,
    move: false,
    restMs: 40
  })

  const click = useClick(context, {
    enabled: false // Disable click to open, but allow click events to pass through
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, click])

  if (!showTooltip) return <>{children}</>

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </div>
      {!!isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              left: x ?? 0,
              position: 'fixed',
              top: y ?? 0,
              zIndex: 1000
            }}
            {...getFloatingProps()}
            className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-md w-72 p-0 rounded-md overflow-hidden"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full p-4 space-y-1">
              {/* First row: Avatar and message button */}
              <div className="flex items-center justify-between">
                <div className="relative">
                  <Avatar
                    src={user.photo || ''}
                    name={user.name || ''}
                    alt={user.name || ''}
                    className="h-12 w-12 border border-slate-100 dark:border-zinc-700"
                  />
                  {!!user?.online && (
                    <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-500 ring-1 ring-white dark:ring-zinc-900" />
                  )}
                </div>

                {!!user.id && user.id !== myDetails?.id && !isUserTypeWorkspace && (
                  <DirectMessageButton receiverId={user.id} />
                )}
              </div>

              {/* Name and verification in one row with proper alignment */}
              <div className="flex items-center">
                <h3 className="font-semibold text-base text-slate-900 dark:text-white">{user.name}</h3>
                {!!workspace?.isVerified && (
                  <CheckCircle className="ml-1.5 h-4 w-4 text-blue-500 fill-blue-100 dark:fill-blue-900" />
                )}
              </div>

              {/* Online status */}
              {!!user?.online && (
                <div className="flex items-center">
                  <span className="flex items-center text-xs text-green-600 dark:text-green-400 font-medium">
                    <span className="mr-1.5 h-2 w-2 rounded-full bg-green-500"></span>
                    Online
                  </span>
                </div>
              )}

              {/* Contact details with consistent spacing */}
              {(!!user.email || !!user.phone) && (
                <div className="space-y-2">
                  {!!user.email && (
                    <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{user.email}</span>
                    </div>
                  )}

                  {!!user.phone && (
                    <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                      <Phone className="h-3.5 w-3.5 shrink-0" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Company Information with improved spacing */}
              {!!workspace?.name && (
                <div className="pt-2.5 mt-0.5 border-t border-slate-100 dark:border-zinc-800">
                  <p className="text-xs font-medium mb-1 text-slate-900 dark:text-slate-200">Company</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">{workspace.name}</p>
                </div>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
